/**
 * @file   : Info.js
 * @author : Amarjeet Lamba (Amarjeet.Lamba@AspiraConnect.com)
 * @date   : 3/25/2019, 7:06:47 PM
 */
import PropTypes from 'prop-types'
import React from 'react'

import Cont from '../../external/components/layout/Cont'
import SimplePageHero from '../shared/header/SimplePageHero'
import PageBase from '../sharedInternal/container/page/PageBase'
import PageContent from '../sharedInternal/container/page/PageContent'
import { getTranslation, isData } from './InfoUtil'
import BigCards from './sections/BigCards'
import Description from './sections/Description'
import DescriptionCards from './sections/DescriptionCards'
import Destinations from './sections/Destinations'
import DropdownCard from './sections/DropdownCard'
import FastFacts from './sections/FastFacts'
import FlipCards from './sections/FlipCards'
import Link from './sections/Link'
import SearchableGrid from './sections/SearchableGrid'
import TextCards from './sections/TextCards'

const InfoLayout = ({ theme, intl, infoData, mainConfig }) => {
  const getHeroUI = function () {
    const titleText = getTranslation(
      intl,
      infoData.title.token,
      infoData.title.default,
      infoData.infoToken,
    )
    const taglineText = getTranslation(
      intl,
      infoData.tagline.token,
      infoData.tagline.default,
      infoData.infoToken,
    )

    return (
      <SimplePageHero
        imageURL={infoData.imageURL}
        titleText={titleText}
        taglineText={taglineText}
      />
    )
  }

  const getLinkUI = function (section) {
    return (
      <Link
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getFastFactsUI = function (section) {
    return (
      <FastFacts
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getDescriptionUI = function (section) {
    return (
      <Description
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getFlipCardSectionUI = function (section) {
    return (
      <FlipCards
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getBigCardSectionUI = function (section) {
    return (
      <BigCards
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getTextCardSectionUI = function (section) {
    return (
      <TextCards
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getDescriptionCardSectionUI = function (section) {
    return (
      <DescriptionCards
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getSearchableGridUI = function (section) {
    return (
      <SearchableGrid
        intl={intl}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
        colLg={4}
        colMd={4}
      />
    )
  }

  const getDropdownCardUI = function (section) {
    return (
      <DropdownCard
        intl={intl}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getDestinationsSectionUI = function (section) {
    return (
      <Destinations
        intl={intl}
        theme={theme}
        section={section}
        mainConfig={mainConfig}
        infoData={infoData}
      />
    )
  }

  const getSectionsUI = function () {
    let ui = null

    if (isData(infoData.sections)) {
      ui = infoData.sections.map((section, i) => {
        let sectionUI = <Cont />

        if (isData(section) && section.showSection) {
          if (section.type === 'flipCards') {
            sectionUI = getFlipCardSectionUI(section)
          } else if (section.type === 'bigCards') {
            sectionUI = getBigCardSectionUI(section)
          } else if (section.type === 'textCards') {
            sectionUI = getTextCardSectionUI(section)
          } else if (section.type === 'descriptionCards') {
            sectionUI = getDescriptionCardSectionUI(section)
          } else if (section.type === 'fastFacts') {
            sectionUI = getFastFactsUI(section)
          } else if (section.type === 'notes') {
            sectionUI = getDescriptionUI(section)
          } else if (section.type === 'link') {
            sectionUI = getLinkUI(section)
          } else if (section.type === 'searchableGrid') {
            sectionUI = getSearchableGridUI(section)
          } else if (section.type === 'destinations') {
            sectionUI = getDestinationsSectionUI(section)
          } else if (section.type === 'dropdownCard') {
            sectionUI = getDropdownCardUI(section)
          }
        }

        return <Cont key={i}>{sectionUI}</Cont>
      })
    }

    return <Cont>{ui}</Cont>
  }

  if (!isData(infoData)) {
    return <Cont></Cont>
  }

  return (
    <PageBase showGenericBG>
      {getHeroUI()}
      <PageContent noHeader>{getSectionsUI()}</PageContent>
    </PageBase>
  )
}

InfoLayout.propTypes = {
  theme: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  infoData: PropTypes.object.isRequired,
  mainConfig: PropTypes.object.isRequired,
}

export default InfoLayout
